import React, { useMemo } from 'react';
import { styled } from '@compiled/react';
import type { OptionProps } from '@atlaskit/select';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { MemoizedInsightIcon } from '@atlassian/jira-servicedesk-insight-icon/src/ui/index.tsx';
import type { CmdbObjectAttribute } from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/attribute.tsx';
import type { CmdbObject } from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/cmdb-object.tsx';

export type Props = OptionProps & {
	attributesToDisplayInSearch: string[];
};

type BoldProps = {
	match: string;
	children: string;
};

export const BoldMatchingText = ({ match, children }: BoldProps) => {
	if (match === '') {
		return <>{children}</>;
	}
	// we need to escape special characters to avoid getting them interpreted in the RegExp group
	const escapedSpecialCharsInMatchQuery = match.replace(/[\\.,*+?^${}()|[\]]/g, '\\$&');
	const parts = children.split(new RegExp(`(${escapedSpecialCharsInMatchQuery})`, 'gi'));
	return (
		<>
			{parts.map((part, i) => {
				if (part.toLowerCase() === match.toLowerCase()) {
					// eslint-disable-next-line @atlaskit/design-system/use-primitives-text
					return <strong key={i}>{part}</strong>;
				}
				return <React.Fragment key={i}>{part}</React.Fragment>;
			})}
		</>
	);
};

export const createAttributeString = (attributes: CmdbObjectAttribute[]) =>
	attributes
		.map(({ objectAttributeValues }) =>
			objectAttributeValues.map(({ displayValue }) => displayValue).join(', '),
		)
		.join(' | ');

export const CmdbSelectOption = ({
	attributesToDisplayInSearch,
	data,
	selectProps,
	label,
}: Props) => {
	// Casting to CmdbObject as typing gets lost when passed through AK Select
	const object: CmdbObject = data.rawData;
	const { inputValue } = selectProps;
	const displayedAttributes = useMemo(
		() =>
			attributesToDisplayInSearch
				.map((attrName) =>
					attrName === 'Label'
						? object.attributes.find((attr) => attr.objectTypeAttribute.label)
						: object.attributes.find((attr) => attr.objectTypeAttribute.name === attrName),
				)
				.filter(Boolean),
		[object, attributesToDisplayInSearch],
	);

	const primaryAttributeString =
		displayedAttributes?.[0]?.objectAttributeValues
			// @ts-expect-error - TS2349 - This expression is not callable.
			?.filter((v: any) => v.displayValue) // eslint-disable-line @typescript-eslint/no-explicit-any
			.map((v: any) => v.displayValue) // eslint-disable-line @typescript-eslint/no-explicit-any
			.join(', ') || label;

	const attributesString = createAttributeString(displayedAttributes.slice(1));

	return (
		<Outer data-testid="servicedesk-cmdb-field-object-picker.ui.cmdb-select-option">
			<IconWrapper>
				<MemoizedInsightIcon
					mediaClientConfig={object.avatar.mediaClientConfig}
					iconUrl={object.avatar.url48}
					label={object.label}
					size="small"
				/>
			</IconWrapper>
			<div>
				<ObjectName data-testid="servicedesk-cmdb-field-object-picker.ui.cmdb-select-option.object-name">
					<BoldMatchingText match={inputValue ?? ''}>{primaryAttributeString}</BoldMatchingText>
				</ObjectName>
				{attributesString && (
					<Attributes data-testid="servicedesk-cmdb-field-object-picker.ui.cmdb-select-option.attributes">
						<BoldMatchingText match={inputValue ?? ''}>{attributesString}</BoldMatchingText>
					</Attributes>
				)}
			</div>
		</Outer>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Outer = styled.div({
	display: 'flex',
	alignItems: 'center',
	padding: `${token('space.100', '8px')} ${token('space.200', '16px')}`,
	'&:hover': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		backgroundColor: token('color.background.neutral.subtle.hovered', colors.N30),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IconWrapper = styled.div({
	marginRight: token('space.150', '12px'),
	flexShrink: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ObjectName = styled.div({
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	fontSize: '14px',
	lineHeight: '24px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Attributes = styled.div({
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	fontSize: '12px',
});
